import type { VisitList } from '@homecare/contracts';
import { addDays, toLocalDate } from '@homecare/domain';
import { Card, EmptyState, Spinner, StatusPill } from '@homecare/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useAgencyToday } from '../../lib/agency-day';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';
import { dayText } from '../../lib/schedule-time';

import { boardState } from './board-layout';

/**
 * A client's visits: the last week and the next four, around the agency's
 * today (R-1e).
 *
 * RECOVERY §3: this tab said "No visits yet" for clients with visits on the
 * board, because it was an empty state and never asked. It asks now, and the
 * empty state only shows when the answer is genuinely empty. Times are the
 * API's local fields; nothing here formats a date by itself.
 */
export function ClientVisits({ clientId }: { clientId: string }): ReactNode {
  const api = useApi();
  const today = useAgencyToday();
  const canRead = useHasPermission('schedule:read');
  const from = today ? addDays(toLocalDate(today), -7) : null;
  const to = today ? addDays(toLocalDate(today), 28) : null;

  const visits = useQuery({
    queryKey: ['schedule', 'client-visits', clientId, from, to],
    queryFn: () =>
      api.get<VisitList>(`/v1/visits?clientId=${clientId}&from=${from ?? ''}&to=${to ?? ''}`),
    enabled: canRead && from !== null,
  });

  if (!canRead) {
    return (
      <Card>
        <EmptyState
          title="Visits are on the schedule"
          explanation="Seeing this client's visits needs the schedule:read permission. A scheduler or an admin can see them on the board."
        />
      </Card>
    );
  }

  return (
    <Card>
      <ErrorNote error={visits.error} nextStep="Reload the page to try again." />
      {visits.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
          <Spinner /> Loading visits…
        </p>
      ) : visits.data && visits.data.visits.length === 0 ? (
        <EmptyState
          title="No visits in these five weeks"
          explanation={`Nothing is booked for this client between ${from ?? ''} and ${to ?? ''}. Book a visit or a weekly pattern from the Schedule.`}
        />
      ) : visits.data ? (
        <>
          <ul className="grid gap-2" aria-label="Visits, last week and the next four">
            {visits.data.visits.map((visit) => {
              const state = boardState(visit);
              return (
                <li
                  key={visit.id}
                  className="border-line flex flex-wrap items-center justify-between gap-2 border-b pb-2 text-sm last:border-0"
                >
                  <span className="text-ink">
                    <span className="font-semibold">{dayText(visit.localDate)}</span>,{' '}
                    {visit.localStartTime}–{visit.localEndTime}
                    {visit.crossesMidnight ? ' (overnight)' : ''} · {visit.serviceCode} ·{' '}
                    {visit.caregiverName ?? 'open shift'}
                  </span>
                  <StatusPill tone={state.tone}>{state.label}</StatusPill>
                </li>
              );
            })}
          </ul>
          <Link
            to="/schedule"
            className="text-brand hover:text-brand-hover mt-3 inline-block text-sm font-medium"
          >
            Open the Schedule
          </Link>
        </>
      ) : null}
    </Card>
  );
}
